import { apiClient } from '@/api/client';
import type {
  CreateTutorProfileRequest,
  UpdateTutorProfileRequest,
  CreateParentProfileRequest,
  ProfileResponse,
  ProfileData,
  GetProfileResponse,
  TutorUploadType,
  TutorUploadAvatarResponse,
  TutorUploadDocumentResponse,
  ParentUploadAvatarResponse,
} from '@/types/api';

/**
 * POST /api/profiles/tutor
 * Create a new tutor profile
 */
export const createTutorProfile = async (
  request: CreateTutorProfileRequest
): Promise<ProfileResponse['data']> => {
  const response = await apiClient.post<ProfileResponse>(
    '/profiles/tutor',
    request
  );

  if (!response.data.success) {
    throw new Error('Failed to create tutor profile');
  }

  return response.data.data;
};

/**
 * POST /api/profiles/parent
 * Create a new parent profile
 */
export const createParentProfile = async (
  request: CreateParentProfileRequest
): Promise<ProfileResponse['data']> => {
  const response = await apiClient.post<ProfileResponse>(
    '/profiles/parent',
    request
  );

  if (!response.data.success) {
    throw new Error('Failed to create parent profile');
  }

  return response.data.data;
};

/**
 * GET /api/profiles/me
 * Fetch authenticated user's profile based on their current role
 * Requires JWT authentication
 */
export const getProfile = async (role: 'tutor' | 'parent'): Promise<ProfileData> => {
  const response = await apiClient.get<GetProfileResponse>(
    '/profiles/me',
    {
      params: { role }
    }
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch profile');
  }

  // API returns { role, profile }, extract the profile object and add role back
  const { role: apiRole, profile } = response.data.data;
  
  // Ensure role is included in profile since API returns { role, profile }
  const profileData: ProfileData = {
    ...profile,
    role: apiRole
  };
  
  return profileData;
};

/**
 * PUT /api/profiles/tutor
 * Update existing tutor profile
 * Requires JWT authentication
 */
export const updateTutorProfile = async (
  updates: UpdateTutorProfileRequest
): Promise<ProfileResponse['data']> => {
  const response = await apiClient.put<ProfileResponse>(
    '/profiles/tutor',
    updates
  );

  if (!response.data.success) {
    throw new Error('Failed to update tutor profile');
  }

  return response.data.data;
};

/**
 * PUT /api/profiles/parent
 * Update existing parent profile
 * Requires JWT authentication
 */
export const updateParentProfile = async (
  updates: Partial<CreateParentProfileRequest>
): Promise<ProfileResponse['data']> => {
  const response = await apiClient.put<ProfileResponse>(
    '/profiles/parent',
    updates
  );

  if (!response.data.success) {
    throw new Error('Failed to update parent profile');
  }

  return response.data.data;
};

export const profilesApi = {
  createTutorProfile,
  createParentProfile,
  getProfile,
  updateTutorProfile,
  updateParentProfile,
};

// ============ File Uploads ============

/**
 * POST /api/profiles/tutor/upload
 * Upload tutor avatar or verification documents.
 * uploadType = 'avatar' → returns photoUrl
 * uploadType = 'identity' | 'qualification' | 'certificate' | 'support' | 'other' → returns documentUrls
 */
export const uploadTutorFile = async (
  uploadType: TutorUploadType,
  files: File[],
  documentNames?: string[]
): Promise<TutorUploadAvatarResponse['data'] | TutorUploadDocumentResponse['data']> => {
  const formData = new FormData();
  formData.append('uploadType', uploadType);
  files.forEach((file, index) => {
    formData.append('files', file);
    if (documentNames?.[index]) {
      formData.append('documentNames', documentNames[index]);
    }
  });

  const response = await apiClient.post<TutorUploadAvatarResponse | TutorUploadDocumentResponse>(
    '/profiles/tutor/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );

  if (!response.data.success) {
    throw new Error(`Failed to upload tutor file (${uploadType})`);
  }

  return response.data.data;
};

/**
 * POST /api/profiles/parent/upload
 * Upload parent profile photo. Returns the new photo URL.
 */
export const uploadParentPhoto = async (
  file: File
): Promise<ParentUploadAvatarResponse['data']> => {
  const formData = new FormData();
  formData.append('files', file);

  const response = await apiClient.post<ParentUploadAvatarResponse>(
    '/profiles/parent/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );

  if (!response.data.success) {
    throw new Error('Failed to upload parent photo');
  }

  return response.data.data;
};

/**
 * GET /api/profiles/tutor/completion-status
 * Get tutor profile completion status and application eligibility
 * Returns: { isProfileComplete, canApply, completionPercentage, verificationStatus }
 */
export interface TutorCompletionMissingField {
  key: string;
  label: string;
  severity: 'required' | 'recommended';
}

export const getTutorCompletionStatus = async (): Promise<{
  isProfileComplete: boolean;
  canApply: boolean;
  completionPercentage: number;
  verificationStatus: 'not_submitted' | 'pending' | 'approved' | 'rejected';
  isFullyVerified: boolean;
  hasAboutMe: boolean;
  missingFields: TutorCompletionMissingField[];
}> => {
  const response = await apiClient.get<{
    success: boolean;
    data: {
      isProfileComplete: boolean;
      canApply: boolean;
      completionPercentage: number;
      verificationStatus: 'not_submitted' | 'pending' | 'approved' | 'rejected';
      isFullyVerified: boolean;
      hasAboutMe: boolean;
      missingFields: TutorCompletionMissingField[];
    };
  }>('/profiles/tutor/completion-status');

  if (!response.data.success) {
    throw new Error('Failed to fetch tutor completion status');
  }

  return response.data.data;
};
